import React, { useState, useEffect } from 'react';
import { 
  Grid, 
  Paper, 
  Typography, 
  Button, 
  Box, 
  Checkbox, 
  FormControlLabel, 
  Slider, 
  Select, 
  MenuItem, 
  Chip,
  CircularProgress,
  Container
} from '@mui/material';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase-config';
import { PhotoCamera, Whatshot } from '@mui/icons-material';

const UsedCars = () => {
  const [priceRange, setPriceRange] = useState([0, 500000]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [transmission, setTransmission] = useState('');
  const [city, setCity] = useState('');
  const [usedCars, setUsedCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCars = async () => {
      try {
        const q = query(
          collection(db, 'ads'), 
          where('carType', '==', 'used'),
          where('status', '==', 'active')
        );
        const querySnapshot = await getDocs(q);
        
        if (querySnapshot.empty) {
          setUsedCars([]);
          return;
        }

        const carsData = querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            price: data.price ? Number(data.price) : 0,
            displayPrice: data.price ? `${data.price.toLocaleString()} €` : 'N/A',
            mileage: data.mileage ? `${data.mileage.toLocaleString()} km` : 'N/A',
            carBodyType: data.carBodyType || '',
            city: data.city || '',
            transmission: data.transmission || '',
            images: data.images || [],
            isPremium: data.isPremium || false,
            isUrgent: data.isUrgent || false
          };
        }).sort((a, b) => {
          // Sort by: urgent > premium > regular
          if (a.isUrgent && !b.isUrgent) return -1;
          if (!a.isUrgent && b.isUrgent) return 1;
          if (a.isPremium && !b.isPremium) return -1;
          if (!a.isPremium && b.isPremium) return 1;
          return 0;
        });
        
        setUsedCars(carsData);
      } catch (err) {
        console.error('Error fetching used cars:', err);
        setError('Грешка при вчитување на податоците. Обидете се повторно.');
      } finally {
        setLoading(false);
      }
    };

    fetchCars();
  }, []);

  const carTypes = ['Седан', 'SUV', 'Хечбек', 'Купе', 'Кабриолет', 'Комби'];
  const cities = [
    'Скопје', 'Битола', 'Прилеп', 'Куманово', 'Тетово', 'Охрид', 'Велес', 'Штип', 'Кавадарци', 'Гостивар',
    'Струмица', 'Неготино', 'Кочани', 'Кичево', 'Ресен', 'Гевгелија', 'Струга', 'Делчево', 'Виница', 'Кратово',
    'Крива Паланка', 'Берово', 'Македонски Брод', 'Дебар', 'Демир Капија', 'Демир Хисар', 'Пробиштип', 'Свети Николе',
    'Валандово', 'Богданци', 'Пехчево', 'Радовиш', 'Теарце', 'Желино', 'Липково', 'Чаир', 'Аеродром', 'Гази Баба',
    'Карпош', 'Кисела Вода', 'Центар', 'Ѓорче Петров', 'Сарај', 'Студеничани', 'Арачиново', 'Сопиште', 'Зелениково',
    'Илинден', 'Петровец', 'Чучер-Сандево', 'Зрновци', 'Маврово и Ростуше', 'Ранковце', 'Лозово', 'Новци', 'Новаци',
    'Конче', 'Дојран', 'Василево', 'Босилово', 'Ново Село', 'Карачиново', 'Куклиш', 'Могила', 'Пласница', 'Центар Жупа'
  ];
  const transmissions = ['Автоматски', 'Мануелен'];

  const filteredCars = usedCars.filter(car => {
    return (
      car.price >= priceRange[0] && 
      car.price <= priceRange[1] &&
      (selectedTypes.length === 0 || selectedTypes.includes(car.carBodyType)) &&
      (!city || car.city === city) &&
      (!transmission || car.transmission === transmission)
    );
  });

  const handlePriceChange = (event, newValue) => {
    setPriceRange(newValue);
  };

  const AdSpace = ({ size, vertical = false }) => (
    <Box sx={{
      p: 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(0,0,0,0.02)',
      border: '1px dashed rgba(0,0,0,0.1)',
      borderRadius: 1,
      textAlign: 'center',
      ...(vertical ? { 
        height: '100%',
        minHeight: 600,
        width: 160
      } : {
        width: '100%',
        height: 90,
        mb: 4
      })
    }}>
      <Typography variant="caption" color="text.secondary">
        Google Ad: {size}
      </Typography>
    </Box>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <Typography color="error" variant="h6">{error}</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Top Leaderboard Ad */}
      <Box sx={{ mb: 4 }}>
        <AdSpace size="970x90 (Leaderboard)" />
      </Box>

      <Box sx={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 4, fontWeight: 'bold' }}>
          Користени Автомобили
        </Typography>
        
        <Grid container spacing={4}>
          {/* Filters Column */}
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 3, position: 'sticky', top: '20px', borderRadius: '12px' }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
                Филтри
              </Typography>
              
              {/* Price Filter */}
              <Box sx={{ mb: 4 }}>
                <Typography gutterBottom sx={{ mb: 2, fontWeight: 'medium' }}>
                  Цена (€)
                </Typography>
                <Slider
                  value={priceRange}
                  onChange={handlePriceChange}
                  valueLabelDisplay="auto"
                  min={0}
                  max={250000}
                  step={500}
                  sx={{ color: '#FF5722' }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">{priceRange[0].toLocaleString()} €</Typography>
                  <Typography variant="body2">{priceRange[1].toLocaleString()} €</Typography>
                </Box>
              </Box>
              
              {/* Car Type Filter */}
              <Box sx={{ mb: 4 }}>
                <Typography gutterBottom sx={{ mb: 2, fontWeight: 'medium' }}>
                  Тип на автомобил
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  {carTypes.map((type) => (
                    <FormControlLabel
                      key={type}
                      control={
                        <Checkbox 
                          checked={selectedTypes.includes(type)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedTypes([...selectedTypes, type]);
                            } else {
                              setSelectedTypes(selectedTypes.filter(t => t !== type));
                            }
                          }}
                          color="primary"
                        />
                      }
                      label={type}
                      sx={{ mb: 1 }}
                    />
                  ))}
                </Box>
              </Box>
              
              {/* City Filter */}
              <Box sx={{ mb: 4 }}>
                <Typography gutterBottom sx={{ mb: 2, fontWeight: 'medium' }}>
                  Град
                </Typography>
                <Select
                  fullWidth
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  displayEmpty
                  sx={{ borderRadius: '8px' }}
                >
                  <MenuItem value="">Сите градови</MenuItem>
                  {cities.map((city) => (
                    <MenuItem key={city} value={city}>{city}</MenuItem>
                  ))}
                </Select>
              </Box>
              
              {/* Transmission Filter */}
              <Box sx={{ mb: 2 }}>
                <Typography gutterBottom sx={{ mb: 2, fontWeight: 'medium' }}>
                  Менувач
                </Typography>
                <Select
                  fullWidth
                  value={transmission}
                  onChange={(e) => setTransmission(e.target.value)}
                  displayEmpty
                  sx={{ borderRadius: '8px' }}
                >
                  <MenuItem value="">Сите типови</MenuItem>
                  {transmissions.map((trans) => (
                    <MenuItem key={trans} value={trans}>{trans}</MenuItem>
                  ))}
                </Select>
              </Box>
            </Paper>

            {/* Vertical Ad Space */}
            <Box sx={{ mt: 4 }}>
              <AdSpace size="300x600 (Skyscraper)" vertical />
            </Box>
          </Grid>
          
          {/* Results Column */}
          <Grid item xs={12} md={6}>
            <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
              {filteredCars.length} {filteredCars.length === 1 ? 'автомобил пронајден' : 'автомобили пронајдени'}
            </Typography>
            
            {filteredCars.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6">Не се пронајдени автомобили според избраните филтри</Typography>
                <Button 
                  variant="contained" 
                  sx={{ mt: 2, bgcolor: '#FF5722' }}
                  onClick={() => {
                    setPriceRange([0, 100000]);
                    setSelectedTypes([]);
                    setTransmission('');
                    setCity('');
                  }}
                >
                  Ресетирај филтри
                </Button>
              </Paper>
            ) : (
              <Grid container spacing={3}>
                {filteredCars.map((car, index) => (
                  <React.Fragment key={car.id}>
                    <Grid item xs={12} sm={6} md={6}>
                      <Paper sx={{ 
                        p: 2, 
                        height: '100%', 
                        display: 'flex', 
                        flexDirection: 'column',
                        borderRadius: '12px',
                        border: car.isUrgent ? '2px solid #FF5722' : '1px solid #e0e0e0',
                        position: 'relative',
                        transition: 'transform 0.3s',
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                        }
                      }}>
                        {car.isUrgent && (
                          <Box sx={{
                            position: 'absolute',
                            top: 10,
                            left: 10,
                            zIndex: 1,
                            display: 'flex',
                            alignItems: 'center'
                          }}>
                            <Whatshot color="error" />
                            <Chip
                              label="ИТНО"
                              size="small"
                              color="error"
                              sx={{ ml: 1, fontWeight: 700 }}
                            />
                          </Box>
                        )}
                        
                        {car.isPremium && (
                          <Chip
                            label="Премиум"
                            color="primary"
                            size="small"
                            sx={{ 
                              position: 'absolute', 
                              top: 10, 
                              right: 10, 
                              zIndex: 1,
                              fontWeight: 'bold'
                            }}
                          />
                        )}
                        
                        {/* Car Image */}
                        <Box sx={{ 
                          height: '180px', 
                          bgcolor: '#f5f5f5', 
                          mb: 2, 
                          borderRadius: '8px',
                          overflow: 'hidden',
                          position: 'relative'
                        }}>
                          {car.images.length > 0 ? (
                            <img 
                              src={car.images[car.coverPhotoIndex || 0]} 
                              alt={car.model} 
                              style={{ 
                                width: '100%', 
                                height: '100%', 
                                objectFit: 'cover' 
                              }}
                            />
                          ) : (
                            <Box sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              height: '100%'
                            }}>
                              <PhotoCamera sx={{ fontSize: 40, color: '#bdbdbd' }} />
                            </Box>
                          )}
                        </Box>
                        
                        {/* Car Details */}
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                            {car.brand} {car.model}
                          </Typography>
                          {car.year && (
                            <Typography variant="subtitle2" sx={{ mb: 1, color: '#757575' }}>
                              Година: {car.year}
                            </Typography>
                          )}
                          <Typography color="primary" sx={{ fontWeight: 'bold', mb: 1.5 }}>
                            {car.displayPrice}
                          </Typography>
                          
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                            <Chip 
                              label={car.mileage} 
                              size="small" 
                              variant="outlined" 
                            />
                            {car.fuelType && (
                              <Chip 
                                label={car.fuelType} 
                                size="small" 
                                variant="outlined" 
                              />
                            )}
                            {car.transmission && (
                              <Chip 
                                label={car.transmission} 
                                size="small" 
                                variant="outlined" 
                              />
                            )}
                          </Box>
                          
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Box component="span" sx={{ mr: 1 }}>📍</Box>
                            {car.city || 'Неодредена локација'}
                          </Typography>
                        </Box>
                        
                        {/* View Details Button */}
                        <Button 
                          component={Link}
                          to={`/cars/${car.id}`}
                          variant="contained"
                          fullWidth
                          sx={{ 
                            mt: 2,
                            bgcolor: '#FF5722',
                            '&:hover': {
                              bgcolor: '#E64A19'
                            }
                          }}
                        >
                          Види детали
                        </Button>
                      </Paper>
                    </Grid>

                    {/* Insert Ad after every 4th car */}
                    {(index + 1) % 4 === 0 && (
                      <Grid item xs={12} sx={{ mt: 2, mb: 4 }}>
                        <AdSpace size="728x90 (Leaderboard)" />
                      </Grid>
                    )}
                  </React.Fragment>
                ))}
              </Grid>
            )}
          </Grid>

          {/* Right Sidebar with Ad */}
          <Grid item xs={12} md={3}>
            <Box sx={{ position: 'sticky', top: 20 }}>
              <AdSpace size="300x600 (Skyscraper)" vertical />
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Bottom Ad Banner */}
      <Box sx={{ mt: 4 }}>
        <AdSpace size="970x90 (Large Leaderboard)" />
      </Box>
    </Container>
  );
};

export default UsedCars;